import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { resolveModule, TYPE_TITLE_MAP } from '../api/notificationsApi';
import { ROLES } from '../config/rbacConfig';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const STORAGE_KEY = 'nlims.workflowNotifications';
const MAX_STORED = 500;

function readLocalNotifications() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function writeLocalNotifications(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(-MAX_STORED)));
  } catch {
    /* storage full or unavailable — degrade silently */
  }
}

// Who should be notified when a record just moved to `newStatus`, based on the
// module's own ROLE_CAN capability table. `rejected`/`approved` notify the
// tier that owns the record (whoever can create + submit it); `submitted`
// notifies validators, `validated` notifies approvers. The actor never
// notifies themselves.
function resolveRecipients(roleCan, newStatus, actorRoleId) {
  if (!roleCan) return [];
  if (newStatus === 'rejected' || newStatus === 'approved') {
    return Object.keys(roleCan).filter(
      (r) => roleCan[r]?.edit && roleCan[r]?.submit && r !== actorRoleId
    );
  }
  const cap = { submitted: 'validate', validated: 'approve' }[newStatus];
  if (!cap) return [];
  return Object.keys(roleCan).filter((r) => roleCan[r]?.[cap] && r !== actorRoleId);
}

function buildMessage(newStatus, actorRoleId, recordLabel, notes) {
  const actorLabel = ROLES[actorRoleId]?.label ?? actorRoleId ?? 'A user';
  const subject = recordLabel ? `Record ${recordLabel}` : 'A record';
  switch (newStatus) {
    case 'submitted':
      return `${subject} was submitted for review by ${actorLabel}.`;
    case 'validated':
      return `${subject} was validated by ${actorLabel} and is awaiting approval.`;
    case 'approved':
      return `${subject} was approved by ${actorLabel}.`;
    case 'rejected':
      return notes
        ? `${subject} was rejected by ${actorLabel}: ${notes}`
        : `${subject} was rejected by ${actorLabel} and needs revision.`;
    default:
      return `${subject} status changed to ${newStatus}.`;
  }
}

function normalizeLocalNotification(raw, viewerRoleId) {
  const created = raw.created_at ?? new Date().toISOString();
  return {
    uuid: raw.id,
    type: raw.type,
    moduleKey: raw.moduleKey,
    moduleLabel: raw.moduleLabel,
    route: raw.route,
    title: raw.title,
    message: raw.message,
    is_read: (raw.readBy ?? []).includes(viewerRoleId),
    created_at: created,
    time: new Date(created),
  };
}

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const roleId = user?.roleId ?? null;

  const [remoteNotifications, setRemoteNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(true);
  const [localRaw, setLocalRaw] = useState(() => readLocalNotifications());

  useEffect(() => {
    setRemoteNotifications([]);
    setNotifLoading(false);
  }, []);

  const refreshNotifications = useCallback(() => {
    setLocalRaw(readLocalNotifications());
  }, []);

  // Keep the badge accurate across tabs (e.g. testing two roles side by side).
  useEffect(() => {
    function onStorage(e) {
      if (e.key === STORAGE_KEY) setLocalRaw(readLocalNotifications());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const id = setInterval(refreshNotifications, 60_000);
    return () => clearInterval(id);
  }, [refreshNotifications]);

  const pushWorkflowNotification = useCallback(({ moduleKey, roleCan, newStatus, actorRoleId, recordLabel, notes }) => {
    const recipientRoles = resolveRecipients(roleCan, newStatus, actorRoleId);
    if (recipientRoles.length === 0) return;

    const { label: moduleLabel, route } = resolveModule(moduleKey);
    const type = `workflow_${newStatus}`;
    const entry = {
      id: `wf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      source: 'local',
      type,
      moduleKey,
      moduleLabel,
      route,
      title: TYPE_TITLE_MAP[type] ?? 'Workflow Update',
      message: buildMessage(newStatus, actorRoleId, recordLabel, notes),
      recipientRoles,
      readBy: [],
      created_at: new Date().toISOString(),
    };

    const next = [...readLocalNotifications(), entry];
    writeLocalNotifications(next);
    setLocalRaw(next);
  }, []);

  const localForViewer = roleId
    ? localRaw.filter((n) => n.recipientRoles?.includes(roleId))
    : [];

  const notifications = [
    ...localForViewer.map((n) => normalizeLocalNotification(n, roleId)),
    ...remoteNotifications,
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const markAsRead = useCallback(async (uuid) => {
    const local = readLocalNotifications();
    const isLocal = local.some((n) => n.id === uuid);
    if (isLocal && roleId) {
      const next = local.map((n) =>
        n.id === uuid && !n.readBy.includes(roleId)
          ? { ...n, readBy: [...n.readBy, roleId] }
          : n
      );
      writeLocalNotifications(next);
      setLocalRaw(next);
      return;
    }
    setRemoteNotifications((prev) => prev.map((n) => (n.uuid === uuid ? { ...n, is_read: true } : n)));
  }, [roleId]);

  const markAllRead = useCallback(async () => {
    if (roleId) {
      const local = readLocalNotifications();
      const next = local.map((n) =>
        n.recipientRoles?.includes(roleId) && !n.readBy.includes(roleId)
          ? { ...n, readBy: [...n.readBy, roleId] }
          : n
      );
      writeLocalNotifications(next);
      setLocalRaw(next);
    }
    setRemoteNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }, [roleId]);

  // Non-workflow events (e.g. a Registry Central proposal) with explicit
  // recipients and a route the card opens. The acting role is never notified.
  const pushNotification = useCallback(({ type = 'general', moduleKey = '', moduleLabel = '', title, message, route, recipientRoles = [], actorRoleId }) => {
    const recipients = recipientRoles.filter((r) => r !== actorRoleId);
    if (recipients.length === 0) return;
    const entry = {
      id: `nt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      source: 'local', type, moduleKey, moduleLabel, route, title, message,
      recipientRoles: recipients, readBy: [], created_at: new Date().toISOString(),
    };
    const next = [...readLocalNotifications(), entry];
    writeLocalNotifications(next);
    setLocalRaw(next);
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications, notifLoading, unreadCount,
      markAsRead, markAllRead, refreshNotifications, pushWorkflowNotification, pushNotification,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
